/**
 * Salesforce Integration Factory
 * Handles creation and registration of Salesforce integrations
 */

import { IntegrationFactory } from '../registry/IntegrationRegistry';
import { SalesforceIntegration, SalesforceConfig } from './SalesforceIntegration';
import { BaseIntegration } from '../base/BaseIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';

export class SalesforceIntegrationFactory implements IntegrationFactory {
  
  async create(config: IntegrationConfig): Promise<BaseIntegration> {
    const salesforceConfig = config as SalesforceConfig;
    
    // Validate required Salesforce-specific configuration
    const validation = await this.validate(salesforceConfig);
    if (!validation.valid) {
      throw new Error(`Invalid Salesforce configuration: ${validation.errors?.map(e => e.message).join(', ')}`);
    }
    
    return new SalesforceIntegration(salesforceConfig);
  }

  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    const errors: string[] = [];
    const salesforceConfig = config as Partial<SalesforceConfig>;

    // Required fields validation
    if (!salesforceConfig.clientId) {
      errors.push('Client ID is required');
    }
    
    if (!salesforceConfig.clientSecret) {
      errors.push('Client Secret is required');
    }
    
    if (!salesforceConfig.redirectUri) {
      errors.push('Redirect URI is required');
    }
    
    // Instance URL is only required for production environments (not sandbox)
    if (!salesforceConfig.sandbox && !salesforceConfig.instanceUrl) {
      errors.push('Instance URL is required for production environments');
    }

    // Validate redirect URI format
    if (salesforceConfig.redirectUri) {
      try {
        new URL(salesforceConfig.redirectUri);
      } catch {
        errors.push('Redirect URI must be a valid URL');
      }
    }

    // Validate API version format
    if (salesforceConfig.apiVersion && !/^v\d+\.\d+$/.test(salesforceConfig.apiVersion)) {
      errors.push('API version must be in format vXX.X (e.g., v58.0)');
    }

    return {
      valid: errors.length === 0,
      errors: errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      properties: {
        clientId: {
          type: 'string',
          title: 'Client ID',
          description: 'Salesforce Connected App Client ID',
          required: true
        },
        clientSecret: {
          type: 'string',
          title: 'Client Secret',
          description: 'Salesforce Connected App Client Secret',
          required: true,
          sensitive: true
        },
        redirectUri: {
          type: 'string',
          title: 'Redirect URI',
          description: 'OAuth redirect URI configured in Salesforce Connected App',
          required: true,
          format: 'uri'
        },
        instanceUrl: {
          type: 'string',
          title: 'Instance URL',
          description: 'Salesforce instance URL (e.g., https://mycompany.salesforce.com)',
          format: 'uri'
        },
        sandbox: {
          type: 'boolean',
          title: 'Sandbox Environment',
          description: 'Whether this is a Salesforce sandbox environment',
          default: false
        },
        apiVersion: {
          type: 'string',
          title: 'API Version',
          description: 'Salesforce API version to use',
          default: 'v58.0',
          pattern: '^v\\d+\\.\\d+$'
        },
        streamingEnabled: {
          type: 'boolean',
          title: 'Enable Streaming API',
          description: 'Enable real-time updates via Salesforce Streaming API',
          default: true
        }
      },
      required: ['clientId', 'clientSecret', 'redirectUri']
    };
  }

  getSupportedFeatures(): string[] {
    return [
      'contacts_sync',
      'activities_logging',
      'custom_fields',
      'real_time_updates',
      'oauth_authentication',
      'streaming_api',
      'field_mapping',
      'conflict_resolution'
    ];
  }
}

// Export singleton instance
export const salesforceIntegrationFactory = new SalesforceIntegrationFactory();